import { Token } from '@remodeling/auth';
import { AppLink, NewDisplayAppLink } from '@display-component/shared';
import {
  WEBAPP_TYPE_WEB,
  WEBAPP_TYPE_AOS,
  WEBAPP_TYPE_IOS,
} from './../constants';
import { BrazeCustomEventProperty, BrazePurchaseProperty } from './braze';

export type WebAppType =
  | typeof WEBAPP_TYPE_WEB
  | typeof WEBAPP_TYPE_AOS
  | typeof WEBAPP_TYPE_IOS;

export interface WebAppPort {
  postMessage: Function;
}

export interface WebAppMessageHandlers {
  callbackHandler: WebAppPort;
}

export interface WebAppWebKit {
  messageHandlers: WebAppMessageHandlers;
}

export interface GeolocationCoordinates {
  accuracy: number;
  altitude: number;
  altitudeAccuracy: number;
  heading: number;
  latitude: number;
  longitude: number;
  speed: number;
}

export interface WebAppNavigator {
  userAgent: string;
  geolocation?: {
    getCurrentPosition: (
      successCallback: ({
        coords,
        timestamp,
      }: {
        coords: GeolocationCoordinates;
        timestamp: number;
      }) => void,
      // 1 : PERMISSION_DENIED
      // 2 : POSITION_UNAVAILABLE
      // 3 : TIMEOUT
      errorCallback?: (error: { code: number; message: string }) => void,
      options?: {
        maximumAge?: number;
        timeout?: number;
        enableHighAccuracy?: boolean;
      },
    ) => void;
  };
}

export interface WebAppWindow {
  addEventListener: Function;
  removeEventListener: Function;
  HSAndroidWeb2App?: {
    postMessage: Function;
  };
  HSAndroidApp2Web?: {
    postMessage: Function;
  };
  webkit?: WebAppWebKit;
  navigator: WebAppNavigator;
}

export type WebAppAction =
  | 'writeLog'
  | 'syncToken'
  | 'share'
  | 'openWebPopUp'
  | 'closeWebPopUp'
  | 'openApp'
  | 'getLocation'
  | 'fireTouchStart'
  | 'fireTouchEnd'
  | 'appsFlyer'
  | 'fireNativeBack'
  | 'brazeLogCustomEvent'
  | 'brazeLogPurchase';

export interface WebAppMessage<T> {
  _id?: number;
  action: WebAppAction;
  data?: T;
  code?: number;
  message?: string;
}

export type WriteLogData = string;

export type SyncTokenData = Token;

export type ShareData = string;

export type OpenWebPopUpData = {
  url: string;
  isReplaceWindow?: boolean;
  shouldFreshWindow?: boolean;
  data?: unknown;
};

export type CloseWebPopUpData = {
  isReloadWindow?: boolean;
};

export type OpenAppData = AppLink;

export type NewOpenAppData = NewDisplayAppLink;

export type GetLocationData =
  | {
      from?: 'navigator' | 'other';
      latitude: number;
      longitude: number;
    }
  | null
  | undefined;

export const AppsFlyerDataKeys = [
  'af_route',
  'af_content',
  'af_content_id',
  'af_order_id',
  'af_price',
  'af_content_type',
  'af_currency',
  'af_quantity',
  'af_login_method',
  'af_registration_method',
] as const;

export type AppsFlyerData<T> = {
  // eslint-disable-next-line no-unused-vars
  [key in typeof AppsFlyerDataKeys[number]]: T;
} & {
  inAppEvent: string;
};

export type AppsFlyerArgs = Partial<
  Omit<AppsFlyerData<unknown>, 'inAppEvent'>
> &
  Pick<AppsFlyerData<unknown>, 'inAppEvent'>;

export type BrazeLogCustomEventData = {
  eventName: string;
  eventProperties?: BrazeCustomEventProperty;
};

export type BrazeLogPurchaseData = {
  productId: string;
  price: number;
  currencyCode: string;
  quantity: number;
  purchaseProperties?: BrazePurchaseProperty;
};
